import {
  FilterList as FilterIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from "@mui/icons-material";
import { Badge, Box, Button, Chip, Menu, MenuItem } from "@mui/material";
import React, { useMemo, useState } from "react";

import {
  ListPracticeUsersRoleEnum,
  ListPracticeUsersStatusEnum,
} from "libs/cellera-sdk";

interface PracticeUserFiltersProps {
  role: string;
  status: string;
  onRoleChange: (role: string) => void;
  onStatusChange: (status: string) => void;
}

interface FilterOption<T> {
  value: T;
  label: string;
}

const ROLE_OPTIONS: FilterOption<ListPracticeUsersRoleEnum | "">[] = [
  { value: "", label: "All Roles" },
  { value: ListPracticeUsersRoleEnum.Patient, label: "Patient" },
  { value: ListPracticeUsersRoleEnum.Provider, label: "Provider" },
  { value: ListPracticeUsersRoleEnum.Staff, label: "Staff" },
  { value: ListPracticeUsersRoleEnum.Admin, label: "Admin" },
];

const STATUS_OPTIONS: FilterOption<ListPracticeUsersStatusEnum | "">[] = [
  { value: "", label: "All Status" },
  { value: ListPracticeUsersStatusEnum.Active, label: "Active" },
  { value: ListPracticeUsersStatusEnum.Inactive, label: "Inactive" },
];

const PracticeUserFilters: React.FC<PracticeUserFiltersProps> = ({
  role,
  status,
  onRoleChange,
  onStatusChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const activeFiltersCount = useMemo(
    () => [role, status].filter(Boolean).length,
    [role, status],
  );

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const clearFilters = () => {
    onRoleChange("");
    onStatusChange("");
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Badge
        color="primary"
        badgeContent={activeFiltersCount}
        invisible={activeFiltersCount === 0}
      >
        <Button
          variant="outlined"
          startIcon={<FilterIcon />}
          endIcon={<ArrowDownIcon />}
          onClick={handleMenuOpen}
          sx={{
            borderColor: "#E5E7EB",
            color: "#374151",
            textTransform: "none",
            "&:hover": {
              borderColor: "#D1D5DB",
              backgroundColor: "#F9FAFB",
            },
          }}
        >
          Filter
        </Button>
      </Badge>

      {activeFiltersCount > 0 && (
        <Chip
          label={`${activeFiltersCount} filter${
            activeFiltersCount > 1 ? "s" : ""
          }`}
          size="small"
          onDelete={clearFilters}
          sx={{
            backgroundColor: "#3B82F6",
            color: "#FFFFFF",
            "& .MuiChip-deleteIcon": {
              color: "#FFFFFF",
            },
          }}
        />
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem disabled dense>
          Role
        </MenuItem>
        {ROLE_OPTIONS.map((option) => (
          <MenuItem
            key={option.value || "all-role"}
            selected={role === option.value}
            onClick={() => onRoleChange(option.value)}
          >
            {option.label}
          </MenuItem>
        ))}
        <MenuItem disabled dense>
          —
        </MenuItem>
        <MenuItem disabled dense>
          Status
        </MenuItem>
        {STATUS_OPTIONS.map((option) => (
          <MenuItem
            key={option.value || "all-status"}
            selected={status === option.value}
            onClick={() => onStatusChange(option.value)}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default PracticeUserFilters;
